<script setup lang="ts" generic="A extends string, B extends string">
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    modelValue: A | B;
    valueA: A;
    valueB: B;
    labelA?: string;
    labelB?: string;
    disabled?: boolean;
    ariaLabel?: string;
    name?: string;
    size?: 'sm' | 'md';
  }>(),
  {
    labelA: 'A',
    labelB: 'B',
    ariaLabel: 'Segment control',
    name: 'segment-control',
    size: 'md',
  }
);

const emits = defineEmits<{ (e: 'update:modelValue', v: A | B): void }>();

const idA = computed(() => `segment-option-${props.valueA}`);
const idB = computed(() => `segment-option-${props.valueB}`);
</script>
<template>
  <div class="seg" role="radiogroup" :aria-label="ariaLabel">
    <input
      :id="idA"
      class="seg__input"
      type="radio"
      :name="name"
      :value="valueA"
      :checked="modelValue === valueA"
      :disabled="disabled"
      @change="$emit('update:modelValue', valueA)"
    />
    <input
      :id="idB"
      class="seg__input"
      type="radio"
      :name="name"
      :value="valueB"
      :checked="modelValue === valueB"
      :disabled="disabled"
      @change="$emit('update:modelValue', valueB)"
    />

    <div
      class="seg__track"
      :class="{
        'is-a': modelValue === valueA,
        'is-b': modelValue === valueB,
        'is-disabled': disabled,
        [`seg__track--${props.size}`]: true,
      }"
    >
      <div class="seg__thumb" :class="`seg__thumb--${props.size}`" aria-hidden="true" />

      <label class="seg__item" :for="idA">
        <span class="seg__text" :class="`seg__text--${props.size}`">{{ labelA }}</span>
      </label>
      <label class="seg__item" :for="idB">
        <span class="seg__text" :class="`seg__text--${props.size}`">{{ labelB }}</span>
      </label>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.seg {
  display: inline-block;
}

.seg__input {
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
  margin: 0;
  pointer-events: none;
}

.seg__track {
  position: relative;
  padding: 4px;
  border-radius: 12px;
  background: #eef2f7;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  overflow: hidden;

  &--sm {
    width: 280px;
    height: 40px;
  }

  &--md {
    width: 340px;
    height: 48px;
  }

  &.is-disabled {
    opacity: 0.6;
    pointer-events: none;
  }
}

.seg__thumb {
  position: absolute;
  top: 4px;
  left: 4px;
  width: calc(50% - 4px);
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0px 2px 4px 0px #0000000d;
  transition: transform 150ms ease;

  &--sm {
    height: 32px;
  }

  &--md {
    height: 40px;
  }
}

.seg__track.is-a .seg__thumb {
  transform: translateX(0);
}
.seg__track.is-b .seg__thumb {
  transform: translateX(100%);
}

.seg__item {
  position: relative;
  z-index: 1;
  display: grid;
  place-items: center;
  cursor: pointer;
  border-radius: 10px;
  user-select: none;
}

.seg__text {
  font-weight: 400;
  line-height: 1;
  padding: 0 10px;
  color: $textSecondary;

  &--sm {
    font-size: 14px;
  }

  &--md {
    font-size: 16px;
  }
}

.seg__track.is-a .seg__item:first-of-type .seg__text,
.seg__track.is-b .seg__item:last-of-type .seg__text {
  color: $textDefault;
  font-weight: 700;
}
</style>
